import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  Typography,
  Container,
  Stack,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
const { REACT_APP_API_ENDPOINT } = process.env;
/**
 * CarLookupWithQR.jsx (clean)
 *
 * Props:
 *  - apiBase: string -> es. process.env.REACT_APP_API_ENDPOINT (senza trailing slash)
 *  - campaigns: array<{ _id: string, denominazione: string }>
 */
export default function CarLookupWithQR({ apiBase = "", campaigns = [] }) {
  // Input & suggerimenti
  const [inputValue, setInputValue] = useState("");
  const [targa, setTarga] = useState("");
  const [options, setOptions] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);

  // Dettaglio auto
  const [autoInfo, setAutoInfo] = useState(null);
  const [loadingAuto, setLoadingAuto] = useState(false);

  // Campagna & update
  const [campaignId, setCampaignId] = useState("");
  const [updating, setUpdating] = useState(false);

  // Campagne disponibili (caricate una volta che l'auto viene trovata)
  const [campaignsData, setCampaignsData] = useState(campaigns);

  // Feedback
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  // QR dialog
  const [qrOpen, setQrOpen] = useState(false);
  const qrInstanceRef = useRef(null);

  // --- API helpers ---
  // Usa il valore passato via prop `apiBase` se presente, altrimenti ripiega
  // sull'endpoint definito in process.env. Questo permette di testare il
  // componente in contesti diversi senza modificare il codice.
  const baseUrl = apiBase || REACT_APP_API_ENDPOINT || "";
  const get = (path, config) => axios.get(`${baseUrl}${path}`, config);
  const post = (path, body, config) => axios.post(`${baseUrl}${path}`, body, config);

  // --- Suggerimenti (debounced) ---
  const fetchSuggest = useMemo(
    () =>
      debounce(async (q) => {
        const query = (q || "").toUpperCase();
        if (query.length < 2) {
          setOptions([]);
          return;
        }
        setLoadingSug(true);
        try {
          const { data } = await get(`/api/cars/suggest`, { params: { q: query } });
          setOptions(Array.isArray(data) ? data : []);
        } catch {
          setOptions([]);
        } finally {
          setLoadingSug(false);
        }
      }, 250),
    [apiBase]
  );

  useEffect(() => {
    fetchSuggest(inputValue);
  }, [inputValue, fetchSuggest]);

  // --- Ricerca dettaglio ---
  const handleSearch = async (maybePlate) => {
    const plate = (maybePlate || targa || "").toUpperCase().replace(/\s+/g, "");
    if (!plate) return;
    setLoadingAuto(true);
    try {
      // Usa la funzione helper `get` passando solo il path relativo; get aggiungerà la base automaticamente
      const { data } = await get(`/api/cars/${encodeURIComponent(plate)}`);
      setAutoInfo(data);
      setSnack({ open: true, msg: "Veicolo trovato", sev: "success" });
    } catch {
      setAutoInfo(null);
      setSnack({ open: true, msg: "Auto non trovata", sev: "warning" });
    } finally {
      setLoadingAuto(false);
    }
  };

  // --- Completa ---
  const handleComplete = async () => {
    if (!autoInfo?._id || !campaignId) {
      setSnack({ open: true, msg: "Seleziona una campagna", sev: "info" });
      return;
    }
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      // Chiama l'endpoint relativo; la funzione post prependerà la base
      await post(
        `/api/auto-campagna/aggiorna`,
        {
          campaignId,
          autoId: autoInfo._id,
          garageId: autoInfo.garageId,
          stato: "completed",
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setSnack({ open: true, msg: "Stato aggiornato con successo", sev: "success" });
    } catch {
      setSnack({ open: true, msg: "Errore aggiornamento stato", sev: "error" });
    } finally {
      setUpdating(false);
    }
  };

  // --- QR dialog lifecycle ---
  useEffect(() => {
    let isMounted = true;
    async function startQR() {
      if (!qrOpen) return;
      try {
        // Import dinamico per evitare errori in ambienti senza DOM
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!isMounted || !qrOpen) return;
        // Assicurati che l'elemento DOM esista
        const el = document.getElementById("qr-reader");
        if (!el) return;
        const instance = new Html5Qrcode("qr-reader");
        qrInstanceRef.current = instance;
        await instance.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            onQRDetected(decodedText);
          },
          () => {}
        );
      } catch {
        setSnack({ open: true, msg: "Impossibile aprire la fotocamera", sev: "error" });
      }
    }

    async function stopQR() {
      const inst = qrInstanceRef.current;
      if (inst) {
        try { await inst.stop(); } catch {}
        try { await inst.clear(); } catch {}
      }
      qrInstanceRef.current = null;
    }

    startQR();
    return () => {
      isMounted = false;
      if (qrOpen) stopQR();
    };
  }, [qrOpen]);

  const onQRDetected = (text) => {
    let plate = String(text || "").trim();
    try {
      const maybe = JSON.parse(plate);
      if (maybe && typeof maybe === "object" && maybe.targa) plate = String(maybe.targa);
    } catch {}
    plate = plate.toUpperCase().replace(/\s+/g, "");
    setTarga(plate);
    setInputValue(plate);
    setQrOpen(false);
    // Avvia subito la ricerca
    handleSearch(plate);
  };

  // Carica la lista campagne quando è stato trovato un veicolo
  useEffect(() => {
    if (!autoInfo) return;
    // Se abbiamo già le campagne, non fare la chiamata di nuovo
    if (campaignsData && campaignsData.length > 0) return;
    const fetchCampaigns = async () => {
      try {
        // Recupera le campagne dal vecchio endpoint generico
        const { data } = await get('/api/campaigns_menu_intallatori');
        setCampaignsData(Array.isArray(data) ? data : []);
      } catch {
        setCampaignsData([]);
        setSnack({ open: true, msg: 'Errore recupero campagne', sev: 'error' });
      }
    };
    fetchCampaigns();
  }, [autoInfo]);

  return (
    <Container maxWidth="sm" sx={{ mt: 2 }}>
      <Stack spacing={2}>
        {/* Autocomplete per la targa */}
        <Autocomplete
          freeSolo
          fullWidth
          disablePortal
          openOnFocus
          options={options}
          inputValue={inputValue}
          onInputChange={(_, v) => {
            const up = (v || "").toUpperCase();
            setInputValue(up);
            setTarga(up);
          }}
          onChange={(_, value) => {
            const targ = typeof value === "string" ? value : value?.targa;
            if (targ) {
              const up = targ.toUpperCase();
              setTarga(up);
              setInputValue(up);
              handleSearch(up);
            }
          }}
          getOptionLabel={(o) => (typeof o === "string" ? o : o?.targa || "")}
          filterOptions={(x) => x}
          noOptionsText="Nessun risultato"
          loading={loadingSug}
          loadingText="Sto cercando…"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cerca per targa"
              placeholder="Es. AB123CD"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (targa?.length) handleSearch();
                }
              }}
              inputProps={{
                ...params.inputProps,
                autoCapitalize: "characters",
                autoCorrect: "off",
                spellCheck: "false",
                inputMode: "text",
                enterKeyHint: "search",
                "aria-label": "Cerca per targa",
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={typeof option === "string" ? option : option.targa} style={{ minHeight: 56, alignItems: "center" }}>
              <strong style={{ width: 96, display: "inline-block" }}>
                {typeof option === "string" ? option : option.targa}
              </strong>
              <span style={{ opacity: 0.8, marginLeft: 8 }}>
                {typeof option === "string"
                  ? ""
                  : option.marca
                  ? `${option.marca} ${option.modello || ""}`
                  : option.modello || "—"}
              </span>
            </li>
          )}
        />

        {/* Pulsante per la scansione QR (verticale) */}
        <Button
          variant="outlined"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => setQrOpen(true)}
        >
          Scansiona QR
        </Button>

        {/* Pulsante per cercare manualmente la targa */}
        <Button variant="contained" onClick={() => handleSearch()} disabled={!targa}>
          Cerca
        </Button>

        {/* Loader del veicolo */}
        {loadingAuto && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} /> Caricamento veicolo…
          </Box>
        )}

        {/* Card con dettagli auto e campagna */}
        {autoInfo && (
          <Card>
            <CardContent>
              <Box display="grid" gap={1}>
                <Typography variant="h6" component="div">
                  {autoInfo.targa}
                </Typography>
                <Typography variant="body2" component="div">
                  Marca/Modello: {autoInfo.marca || '—'} {autoInfo.modello || ''}
                </Typography>
                <Typography variant="body2" component="div">
                  Garage ID: {autoInfo.garageId || '—'}
                </Typography>
                <Typography variant="body2" component="div">
                  Recapito: {autoInfo.recapito || '—'}
                </Typography>

                <FormControl size="small" sx={{ maxWidth: 360, mt: 1 }}>
                  <InputLabel id="campaign-label">Campagna</InputLabel>
                  <Select
                    labelId="campaign-label"
                    label="Campagna"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                  >
                    {campaignsData.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.denominazione}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <Button
                    variant="contained"
                    onClick={handleComplete}
                    disabled={updating || !campaignId}
                  >
                    {updating ? 'Aggiorno…' : 'Segna come completata'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Dialog QR (usa html5-qrcode) */}
        <Dialog open={qrOpen} onClose={() => setQrOpen(false)} fullWidth>
          <DialogTitle>Scansiona QR</DialogTitle>
          <DialogContent>
            <div id="qr-reader" style={{ width: "100%", maxWidth: 380, margin: "0 auto" }} />
            <Box mt={1} fontSize={14} color="text.secondary">
              Inquadra il QR dell’automezzo. Supporta payload "GJ366FD" o JSON {"{"}"targa":"GJ366FD"{"}"}.
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQrOpen(false)}>Chiudi</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar per feedback */}
        <Snackbar
          open={snack.open}
          autoHideDuration={2500}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.sev} variant="filled" sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Stack>
    </Container>
  );
}
